import { useEffect } from 'react';
import FormCheckbox from 'components/common/forms/FormCheckbox';
import extraIntraOralExaminationImg from 'assets/icons/extra_intra_oral_examination_chart.png';
import styles from 'styles/pages/chart/ExtraIntraOralExaminationChart.module.scss';
import classNames from 'classnames';
import { STYLES_VARIABLES } from 'constants/styleVariables';
import {
  ExtraOralExamination,
  IntraOralExamination,
  ExtraIntraOralExaminationChartFields,
} from 'constants/chart/extraIntraOralExaminationChart';
import { TTool } from 'types/apis/chart/studentPracticeChart';
import { TLine } from 'types/chart/pentool';
import useCanvas from 'hooks/chart/useCanvas';
import { Layer, Stage, Line, Image as KonvaImg } from 'react-konva';

type TProps = {
  tool: TTool;
  strokeWidth: number;
  setStrokeWidth: (strokeWidth: number) => void;
  strokeColor: string;
  strokeActive: boolean;
  lines: TLine[];
  setLines: (lines: TLine[]) => void;
  stageRef: any;
  penLayerRef: any;
  isProfessor: boolean;
  isRegister: boolean;
  isReadOnly: boolean;
};

const ExtraIntraOralExaminationChart = ({
  tool,
  strokeWidth,
  setStrokeWidth,
  strokeColor,
  strokeActive,
  lines,
  setLines,
  stageRef,
  penLayerRef,
  isProfessor,
  isRegister,
  isReadOnly,
}: TProps) => {
  const { handleTouchStart, handleTouchMove, handleTouchEnd, useImage } =
    useCanvas({
      tool,
      strokeWidth,
      strokeColor,
      lines,
      setLines: (newLines) => {
        if (!isRegister || isReadOnly) return;
        setLines(newLines);
      },
    });

  const handleTouchStartWrapper = (e: any) => {
    if (!isRegister || isReadOnly || !strokeActive) return;
    handleTouchStart(e);
  };

  const handleTouchMoveWrapper = (e: any) => {
    if (!isRegister || isReadOnly || !strokeActive) return;
    handleTouchMove(e);
  };

  const handleTouchEndWrapper = (e: any) => {
    if (!isRegister || isReadOnly || !strokeActive) return;
    handleTouchEnd(e);
  };

  const [chartImg] = useImage(extraIntraOralExaminationImg);

  useEffect(() => {
    if ((isRegister && !isProfessor) || isProfessor) {
      setStrokeWidth(3);
    } else {
      setStrokeWidth(0);
    }
  }, [isRegister]);

  const extraKeys = Object.keys(ExtraOralExamination);
  const intraKeys = Object.keys(IntraOralExamination);
  const keyLength =
    extraKeys.length > intraKeys.length ? extraKeys.length : intraKeys.length;
  const extraLabels = Object.values(ExtraOralExamination);
  const intraLabels = Object.values(IntraOralExamination);

  const rendering = () => {
    const tableList = [];

    for (let i = 0; i < keyLength; i++) {
      tableList.push(
        <tr key={i} className={styles.tbody_tr}>
          <td className={styles.td} style={{ color: STYLES_VARIABLES.GRAY_07 }}>
            {i + 1}
          </td>
          <td className={styles.td}>{extraLabels[i] || '-'}</td>
          <td className={styles.td}>
            {extraLabels.length > i ? (
              <FormCheckbox
                name={ExtraIntraOralExaminationChartFields.ExtraOralExamination}
                text="WNL"
                value={extraKeys[i]}
                multiple={true}
                readOnly={!isRegister || isReadOnly}
              />
            ) : (
              '-'
            )}
          </td>
          <td className={styles.td}>{intraLabels[i] || '-'}</td>
          <td className={styles.td}>
            {intraLabels.length > i ? (
              <FormCheckbox
                name={ExtraIntraOralExaminationChartFields.IntraOralExamination}
                text="WNL"
                value={intraKeys[i]}
                multiple={true}
                readOnly={!isRegister || isReadOnly}
              />
            ) : (
              '-'
            )}
          </td>
        </tr>
      );
    }
    return tableList;
  };

  return (
    <>
      <div className="flex flex_column">
        <table className={styles.table}>
          <colgroup>
            <col width="60" />
            <col width="*" />
            <col width="100" />
            <col width="*" />
            <col width="100" />
          </colgroup>
          <thead>
            <tr className={styles.thead_tr}>
              <th className={styles.th}></th>
              <th className={styles.th} colSpan={2}>
                Extra-oral examination
              </th>
              <th className={styles.th} colSpan={2}>
                Intra-oral examination
              </th>
            </tr>
          </thead>
          <tbody>{rendering()}</tbody>
        </table>

        <Stage
          ref={stageRef}
          width={920}
          height={720}
          className={classNames(styles.image_area, 'chart_image_area')}
          onTouchStart={handleTouchStartWrapper}
          onTouchMove={handleTouchMoveWrapper}
          onTouchEnd={handleTouchEndWrapper}
        >
          <Layer>
            {chartImg && (
              <KonvaImg
                className={styles.image_area}
                image={chartImg}
                x={140}
                y={50}
                width={643}
                height={620}
              />
            )}
          </Layer>
          <Layer ref={penLayerRef}>
            {lines.map((line, i) => (
              <Line
                key={i}
                points={line.points}
                stroke={line.strokeColor}
                strokeWidth={line.strokeWidth}
                tension={0.5}
                lineCap="round"
                lineJoin="round"
                globalCompositeOperation={
                  line.tool === 'eraser' ? 'destination-out' : 'source-over'
                }
              />
            ))}
          </Layer>
        </Stage>
      </div>
    </>
  );
};

export default ExtraIntraOralExaminationChart;
